import { useState, useEffect } from "react";
import { useApi } from "@/lib/useApi";
import { api, apiErr } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, EmptyState } from "@/components/common";
import { StatusPill } from "@/components/StatusPill";
import { Boxes, Cog, Hand, RefreshCw, ArrowRight, Clock, UserPlus } from "lucide-react";
import { toast } from "sonner";

function DelegateManager() {
  const { t } = useLang();
  const [list, setList] = useState(null);
  const load = () => api.get("/production/shift-demand/delegates").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const toggle = async (u, val) => {
    try { await api.post("/production/shift-demand/delegates", { user_id: u.id, enabled: val }); load(); toast.success(t("updated")); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Card className="mt-4 p-4" data-testid="delegate-manager">
      <div className="mb-1 flex items-center gap-2 text-sm font-semibold"><UserPlus className="h-4 w-4 text-accent" />{t("delegate_q")}</div>
      <p className="mb-3 text-xs text-muted-foreground">{t("delegate_hint")}</p>
      <div className="space-y-1">
        {(list || []).map((u) => (
          <label key={u.id} className="flex items-center gap-3 rounded-md px-2 py-1.5 hover:bg-secondary/50" data-testid={`delegate-${u.id}`}>
            <Checkbox checked={u.is_delegate} onCheckedChange={(v) => toggle(u, !!v)} data-testid={`delegate-toggle-${u.id}`} />
            <span className="flex-1 text-sm">{u.name} <span className="text-xs text-muted-foreground">· {u.email}</span></span>
            {u.is_delegate && <Badge variant="secondary">{t("orderer")}</Badge>}
          </label>
        ))}
      </div>
    </Card>
  );
}

function MoveRow({ mv, i, shifts, onApprove }) {
  const { t } = useLang();
  const [date, setDate] = useState("");
  const [shiftId, setShiftId] = useState("");
  const opts = (shifts || []).filter((s) => s.department_id === mv.to_department_id);
  const go = async () => {
    if (!date || !shiftId) return toast.error(t("move_pick"));
    await onApprove({ employee_id: mv.employee_id, to_department_id: mv.to_department_id, from_department_id: mv.from_department_id, date, shift_id: shiftId });
  };
  return (
    <div className="rounded bg-secondary/40 px-2 py-2 text-sm" data-testid={`move-${i}`}>
      <div className="flex items-center gap-2">
        <span className="font-medium">{mv.employee}</span>
        <span className="text-muted-foreground">{mv.from_department || "—"}</span>
        <ArrowRight className="h-3.5 w-3.5" />
        <span>{mv.to_department}</span>
      </div>
      <div className="mt-1.5 flex flex-wrap items-center gap-2">
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-8 w-36" data-testid={`move-date-${i}`} />
        <Select value={shiftId} onValueChange={setShiftId}>
          <SelectTrigger className="h-8 w-28" data-testid={`move-shift-${i}`}><SelectValue placeholder={t("move_shift_ph")} /></SelectTrigger>
          <SelectContent>{opts.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
        </Select>
        <Button size="sm" onClick={go} data-testid={`move-approve-${i}`}>{t("move_approve")}</Button>
      </div>
    </div>
  );
}

export default function Production() {
  const { t } = useLang();
  const MODES = { manual: t("mode_manual"), ewm: t("mode_ewm"), both: t("mode_both") };
  const { data: mode, reload: rMode } = useApi("/production/mode");
  const { data: depts } = useApi("/departments");
  const { data: demand, reload: rDemand } = useApi("/staffing-demand");
  const { data: dist, reload: rDist } = useApi("/staffing/distribution");
  const { data: shifts } = useApi("/shifts");
  const { data: access } = useApi("/production/shift-demand/access");
  const { data: areas } = useApi("/business_areas");
  const adminAreaIds = new Set((areas || []).filter((a) => a.type === "administration").map((a) => a.id));
  const bemanningDepts = (depts || []).filter((d) => !adminAreaIds.has(d.business_area_id));

  const m = mode?.mode || "manual";
  const demandFor = (deptId) => (demand || []).find((d) => d.department_id === deptId && !d.shift_id);
  const shiftDemandFor = (deptId, shiftId) => (demand || []).find((d) => d.department_id === deptId && d.shift_id === shiftId);
  const shiftsForDept = (deptId) => (shifts || []).filter((s) => s.department_id === deptId);

  const setMode = async (v) => { try { await api.put("/production/mode", { mode: v }); rMode(); toast.success(t("mode_saved")); } catch (e) { toast.error(apiErr(e)); } };
  const saveDemand = async (deptId, val) => {
    try { await api.put("/staffing-demand", { department_id: deptId, required_people: Number(val) || 0 }); rDemand(); rDist(); toast.success(t("demand_saved")); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const saveShiftDemand = async (deptId, shiftId, val) => {
    try { await api.put("/staffing-demand", { department_id: deptId, shift_id: shiftId, required_people: Number(val) || 0 }); rDemand(); toast.success(t("shift_demand_saved")); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const ewmSync = async () => {
    try { const r = await api.post("/production/ewm-sync"); toast.message(r.data.note); rDemand(); rDist(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const approveMove = async (payload) => {
    try { await api.post("/staffing/approve-move", payload); toast.success(t("pass_created")); rDist(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div>
      <PageHeader title={t("prod_title")} icon={Boxes} subtitle={t("prod_sub")} />

      <Card className="mb-4 flex flex-wrap items-center gap-3 p-4">
        <span className="text-sm font-semibold">{t("demand_source")}</span>
        <Select value={m} onValueChange={setMode}>
          <SelectTrigger className="w-[360px]" data-testid="demand-mode"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(MODES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
        </Select>
        {(m === "ewm" || m === "both") && (
          <Button variant="outline" onClick={ewmSync} data-testid="ewm-sync-btn"><RefreshCw className="mr-1 h-4 w-4" />{t("ewm_fetch")}</Button>
        )}
      </Card>

      {(depts || []).length === 0 ? (
        <EmptyState icon={Boxes} title={t("prod_empty_title")} text={t("prod_empty_text")} />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          <div>
            <h2 className="mb-2 font-head text-lg font-semibold flex items-center gap-2"><Hand className="h-4 w-4 text-accent" />{t("demand_per_dept")}</h2>
            <div className="space-y-2">
              {bemanningDepts.map((d) => {
                const dem = demandFor(d.id);
                const editable = m === "manual" || m === "both";
                return (
                  <Card key={d.id} className="flex items-center gap-3 p-3" data-testid={`demand-${d.id}`}>
                    <div className="flex-1"><div className="font-medium">{d.name}</div>
                      {dem && <div className="text-xs text-muted-foreground">{t("source_label", { src: dem.source === "ewm" ? t("source_ewm") : t("source_manual") })}{dem.status === "förslag" && t("proposal_suffix")}</div>}
                    </div>
                    <Input type="number" min="0" defaultValue={dem?.required_people ?? ""} disabled={!editable}
                      className="w-24" placeholder={t("count_ph")} data-testid={`demand-input-${d.id}`}
                      onBlur={(e) => editable && saveDemand(d.id, e.target.value)} />
                    <span className="text-xs text-muted-foreground">{t("pers")}</span>
                  </Card>
                );
              })}
            </div>
          </div>

          <div>
            <h2 className="mb-2 font-head text-lg font-semibold flex items-center gap-2"><Cog className="h-4 w-4 text-accent" />{t("proposed_dist")}</h2>
            <Card className="p-4">
              <div className="space-y-2">
                {(dist?.departments || []).map((r) => (
                  <div key={r.department_id} className="flex items-center gap-3 border-b py-2 text-sm last:border-0" data-testid={`dist-${r.department_id}`}>
                    <span className="flex-1 font-medium">{r.department}</span>
                    <span className="font-mono text-xs text-muted-foreground">{t("need_have", { req: r.required, av: r.available })}</span>
                    {r.gap > 0 ? <Badge variant="destructive">{t("missing", { n: r.gap })}</Badge> : r.required > 0 ? <StatusPill level="gron" /> : <Badge variant="secondary">{t("not_set")}</Badge>}
                  </div>
                ))}
              </div>
              {(dist?.moves || []).length > 0 && (
                <div className="mt-4">
                  <div className="mb-1 text-sm font-semibold">{t("proposed_moves")}</div>
                  <div className="space-y-2">
                    {dist.moves.map((mv, i) => <MoveRow key={i} mv={mv} i={i} shifts={shifts} onApprove={approveMove} />)}
                  </div>
                </div>
              )}
              <p className="mt-3 text-xs text-muted-foreground">{dist?.note}</p>
            </Card>
          </div>
        </div>
      )}

      {access?.can_edit && (depts || []).length > 0 && (
        <Card className="mt-4 p-4" data-testid="shift-demand-card">
          <h2 className="mb-1 font-head text-lg font-semibold flex items-center gap-2"><Clock className="h-4 w-4 text-accent" />{t("demand_per_shift")}</h2>
          <p className="mb-3 text-xs text-muted-foreground">{t("demand_per_shift_hint")}</p>
          {bemanningDepts.filter((d) => shiftsForDept(d.id).length > 0).length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("no_shifts_defined")}</p>
          ) : (
            <div className="space-y-3">
              {bemanningDepts.filter((d) => shiftsForDept(d.id).length > 0).map((d) => (
                <div key={d.id} className="rounded-lg border p-3" data-testid={`shift-demand-dept-${d.id}`}>
                  <div className="mb-2 text-sm font-medium">{d.name}</div>
                  <div className="flex flex-wrap gap-3">
                    {shiftsForDept(d.id).map((s) => {
                      const sd = shiftDemandFor(d.id, s.id);
                      return (
                        <div key={s.id} className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">{s.name}</span>
                          <Input type="number" min="0" defaultValue={sd?.required_people ?? ""} className="w-20"
                            placeholder={t("count_ph")} data-testid={`shift-demand-input-${d.id}-${s.id}`}
                            onBlur={(e) => saveShiftDemand(d.id, s.id, e.target.value)} />
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {access?.can_manage && <DelegateManager />}
    </div>
  );
}
